// Pure draft reducer + validation for the alarm CREATE / EDIT form (UI-3).
//
// Kept entirely pure (no React) so the whole authoring surface — mode union,
// per-mode threshold fan-out, bounds validation, and the minimal-PATCH diff —
// is node-tested without a DOM. The web build carries the recipe param metadata
// (label/unit/bounds/perMode) over the wire in the draft's `params`, so a param
// with `perMode: false` fans a single value out to every mode (the API stores
// thresholds keyed by mode, and every enabled mode must carry every param key).
//
// The two body builders are deliberately asymmetric:
//   - `toCreateBody`  → the full POST body.
//   - `toPatchBody`   → CHANGED FIELDS ONLY. A full-body PATCH that re-sent
//                       untouched fields would reset a value another surface
//                       (or the boat) just changed — a data-loss bug — and would
//                       needlessly recompile+redeliver the ruleset.
//
// UNITS. `AlarmDraft.values` holds what is IN THE BOXES — the owner's unit for
// a param the registry marks `display` (seconds, minutes, percent) or that the
// ACCOUNT reads in its own unit (a depth in feet, an engine temperature in °F —
// ADR 0189 §4), the stored unit for every other one. The conversion happens at
// the two edges only: `initFromDef` reads stored→edited, and `buildThresholds`
// writes edited→stored. Everything between them — fan-out, the per-mode split,
// the collapse warning — moves strings around and never needs to know. Crucially
// the PATCH DIFF runs on the stored side (`buildThresholds` output against
// `original`), so opening an alarm and saving it untouched still produces an
// empty patch; diffing the edited strings would have made every rounding
// artefact a spurious rewrite of the boat's ruleset.
//
// THE BASELINE GUARD (ADR 0189 §4) is what lets an account-unit box round its
// number for legibility. A converted rendering does not round-trip — a stored
// 20 m depth reads "66 ft", and 66 ft is 20.1168 m — so `initFromDef` captures,
// per mode and per param, the exact TEXT it put in the box beside the NUMBER
// that text was rendered from. `storedNumber` re-emits that original number
// verbatim for any box still holding its baseline text (or a numerically equal
// retype of it), with no arithmetic at all.
//
// This is the load-bearing invariant of the whole change, and it is stronger
// than "the patch is usually empty": `thresholdsByMode` is ALL-OR-NOTHING, so an
// edit to one key re-emits every other key in every enabled mode. Without the
// guard, one owner changing a debounce would rewrite the depth threshold beside
// it by a rounding artefact, move the definition digest, and resync the boat's
// entire ruleset. Every path that turns a box into a number goes through the one
// resolver for exactly this reason — a second parser that skipped the baseline
// would put the on-screen evaluation notice and the wire body into disagreement
// about a field nobody touched.

import type { UnitSettings } from '@neomaris/protocol';
import {
  CANONICAL_MODES,
  foldModesToCanonical,
  isContactStateParam,
  HATCH_SECURITY_LABEL,
  SMOKE_CO_GAS_DETECTOR_LABEL,
} from '@neomaris/rules';

import { readConditions, type ConditionClauseSpec } from './alarmSentence';
// `readBindingPath` lives beside the `binding` field it reads (alarmDisplay), so
// the editor and every read-only surface share ONE defensive reader.
import { readBindingPath, type AlarmDefView, type AlarmSeverity } from './alarmDisplay';
import {
  conditionSupport,
  DEFAULT_ENGINE_RUNNING_RPM,
  formatEditedNumber,
  paramEditorUnits,
  paramValueText,
  templateRequiresPin,
  type AlertTemplate,
  type RecipeConditionMeta,
  type RecipeParamMeta,
  type RecipePathFamilyMeta,
} from './alertTemplates';
import { signalLabel, type Signal } from './signalCatalog';
// The create form's opening mode set has to agree with the sentence the watch
// field will print for it — see `initFromTemplate`.
import { detectWatchPreset, toggleWatchGridMode, watchPresetModes } from './watchSchedule';

/**
 * The v1 clause cap the shared schema enforces (`alertConditionsSchema`, 1..4).
 * Mirrored so the form refuses to build a body the API would 400.
 */
export const MAX_CONDITION_CLAUSES = 4;

/**
 * The highest RPM floor the form accepts. Not a compiler bound — a sanity
 * bound: a marine engine that counts as "running" above 10 000 RPM is a typo,
 * and a typo here silently disarms a safety alarm (the clause never reads true).
 */
export const MAX_ENGINE_RUNNING_RPM = 10_000;

/**
 * One box's original state: the TEXT `initFromDef` put in it, and the stored
 * NUMBER that text was rendered from. Always a pair — the text is always a
 * rendering of that number, never of a different mode's — because
 * {@link storedNumber} answers with the number whenever it still sees the text.
 */
export interface ParamBaseline {
  readonly text: string;
  readonly stored: number;
}

/** The editable draft for one alarm definition. */
export interface AlarmDraft {
  readonly recipeId: string;
  /** Empty ⇒ inherit the recipe's default title (create only; edit requires a name). */
  readonly title: string;
  readonly severity: AlarmSeverity;
  /** Every selectable mode: the whole canonical vocabulary (see {@link RECIPE_MODE_UNIVERSE}). */
  readonly modeUniverse: string[];
  /** The checked subset of {@link modeUniverse}, in universe order. */
  readonly enabledModes: string[];
  /**
   * Raw string threshold values keyed `[mode][paramKey]` (parsed on
   * validate/submit), IN THE UNIT THE BOX SHOWS — see the units note at the top
   * of this file. Never assume milliseconds here.
   */
  readonly values: Record<string, Record<string, string>>;
  /**
   * What each box held when the draft was built, keyed `[mode][paramKey]` like
   * {@link values} — see the baseline note at the top of this file. Only for the
   * boxes a stored number actually filled: a blank box, and every box on a
   * create draft, has no entry.
   *
   * OPTIONAL because a draft assembled by hand (the inline-editor tests) is
   * still a valid draft: with no baseline every box simply converts, which is
   * exactly the behaviour that predates ADR 0189.
   */
  readonly baseline?: Record<string, Record<string, ParamBaseline>>;
  /**
   * The account's display units, carried on the draft like {@link params} so the
   * pure reducer can convert without a React context. Absent ⇒ no account
   * conversion, i.e. every box shows the stored number (what a metric account
   * sees anyway).
   */
  readonly units?: UnitSettings;
  /** Recipe param display metadata carried from the template (bounds + perMode). */
  readonly params: RecipeParamMeta[];
  /**
   * The param keys the operator has chosen to break out PER MODE. Everything not
   * listed carries one shared number, fanned out to every mode on save.
   *
   * Empty by default, and that is the fix for the form this replaced. Every
   * recipe param in the registry is declared `perMode: true`, and the form read
   * that as "render one input per enabled mode" — so "House battery low" with
   * its five default modes opened with TEN boxes to fill (a voltage and a
   * debounce, five times over) before it would submit. Almost nobody wants a
   * different low-voltage floor at anchor than at the dock; they want one
   * number. `perMode: true` means the split is ALLOWED, not that it is wanted.
   *
   * It is doubly right here because v1's compiler collapses a definition to one
   * rule and evaluates only the primary mode's numbers anyway (see
   * {@link perModeEvaluationNotice}) — the old default asked for four numbers it
   * would then ignore.
   */
  readonly splitModes: readonly string[];
  /**
   * The recipe's condition-preset metadata, when it takes one (the engine
   * recipes). Absent ⇒ the form shows no condition toggle, and the draft can
   * never author a preset the API would reject.
   */
  readonly conditionSupport?: RecipeConditionMeta;
  /** Whether the engine-running preset is attached to this alarm. */
  readonly engineCondition: boolean;
  /** The RPM floor for the preset, raw string (parsed on validate/submit). */
  readonly minRpm: string;
  /**
   * Condition clauses this form does NOT author — explicit path conditions on a
   * recipe def (which the API accepts but only the custom builder writes), and
   * a stored engine preset on a recipe whose library metadata does not declare
   * support for one. Carried verbatim so saving an unrelated edit here can
   * never silently drop someone else's clause — or a safety condition this
   * build is simply too old to render.
   */
  readonly otherConditionClauses: readonly ConditionClauseSpec[];
  /** The stored fold (`all` / `any`), preserved for the same reason. */
  readonly conditionLogic: 'all' | 'any';
  /**
   * The concrete path this alarm is PINNED to (`binding.path`, ADR-097 §4), or
   * `''` for "any" — today's family first-match, which is what every alarm
   * authored before pins existed does and stays the default.
   *
   * A pin is what makes "starboard engine overtemp" mean the starboard engine
   * on a boat that reports two. It is HARD in the compiler: a pinned path that
   * stops being reported compiles pending rather than re-pointing at the
   * sibling that is still alive.
   */
  readonly binding: string;
  /**
   * The recipe's path family, carried from the template (like
   * {@link conditionSupport}) so {@link bindingChoices} can offer exactly the
   * paths the compiler would accept. Absent ⇒ this recipe watches one fixed
   * path, or this build's library predates the metadata: either way the form
   * asks nothing and the stored pin (if any) rides through untouched.
   */
  readonly bindingFamily?: RecipePathFamilyMeta;
  /**
   * `true` ⇒ this recipe REFUSES to run unpinned, so the form must ask and must
   * not offer "Automatic" (core ADR-116 part A, `AlertTemplate.requiresPin`).
   *
   * Only `bilge_high` today. Its family is every switch channel aboard, so
   * "whichever one is reported first" is as likely to be the cabin lights as the
   * float switch — an alarm that fires when someone turns on a light and stays
   * silent while the bilge fills. The API refuses an unpinned one (`422
   * invalid_binding`); this field is what stops the form building that request
   * in the first place, and what lets it explain why.
   */
  readonly bindingRequired?: true;
}

/**
 * The ordered union of two mode lists, de-duplicated, first-seen order. Mode
 * vocabulary is ALWAYS derived from the template defaults ∪ the definition's
 * enabled modes — never a hardcoded list.
 *
 * Every id is CANONICALIZED through the shared fold, so a definition stored with
 * the FTUE's lowercase vocabulary renders as one checkbox per mode (not a
 * doubled TitleCase+lowercase list) and the PATCH carries modes the API's
 * `z.enum(CANONICAL_MODES)` accepts. Canonicalizing — rather than "first
 * spelling wins" — matters for a mode the TEMPLATE does not list: e.g.
 * `anchor_drift` defaults to `["Anchored"]`, so a stale row holding
 * `["anchored","underway"]` would otherwise keep `"underway"` verbatim and the
 * PATCH would 400 on exactly the stale row this is meant to rescue.
 *
 * An id outside the canonical vocabulary is dropped by the fold, so it can never
 * reach the wire. Storage is canonicalized on ingest + backfill; this is the
 * belt-and-braces for a row written before that.
 */
export function modeUnion(
  defaultModes: readonly string[],
  defModes: readonly string[] = []
): string[] {
  return foldModesToCanonical(
    [...defaultModes, ...defModes].filter((m) => typeof m === 'string' && m.length > 0)
  );
}

/**
 * Every mode the recipe editor offers as a checkbox: the WHOLE canonical
 * vocabulary, for every recipe.
 *
 * A recipe's `defaultModes` is a RECOMMENDATION, not a permission. It seeds
 * which boxes start checked; it must never decide which boxes exist. Deriving
 * the universe from `defaultModes` (as this form did) silently withheld modes
 * the operator is entitled to pick — "low engine oil pressure" offered only
 * Underway + Maintenance, so an operator who wanted it watched at anchor had no
 * way to say so, and `Sailing` was unreachable in this editor entirely despite
 * being elected at runtime and accepted by both write APIs.
 *
 * Matches {@link CUSTOM_MODE_UNIVERSE} in `customAlarmForm.ts` — a custom alarm
 * already offered all six. There was no reason a recipe-backed alarm offered
 * fewer.
 *
 * Both write APIs already validate against the same canonical set with no
 * per-recipe narrowing (`core/src/api/alerts-defs.ts` `parseEnabledModes`,
 * `cloud/src/api/alerts/routes.ts` `canonicalModeSchema`), so nothing here can
 * build a body the server rejects.
 */
export const RECIPE_MODE_UNIVERSE: readonly string[] = CANONICAL_MODES;

/** Case-insensitive lookup into a per-mode record, so a draft keyed by the
 *  canonical spelling still finds a block a legacy row stored lowercase. */
export function blockForMode<V>(
  byMode: Record<string, V> | undefined,
  mode: string
): V | undefined {
  if (!byMode) return undefined;
  const direct = byMode[mode];
  if (direct !== undefined) return direct;
  const key = Object.keys(byMode).find((k) => k.toLowerCase() === mode.toLowerCase());
  return key === undefined ? undefined : byMode[key];
}

/**
 * A fresh CREATE draft from a library template (severity + modes prefilled).
 *
 * `units` is REQUIRED, and required for the same reason `displayUnits` refuses
 * to default them: a caller that forgets to thread the account's units must fail
 * to compile rather than quietly author a metric form for an imperial owner.
 * There is no baseline here — every box starts empty, so there is nothing an
 * owner could leave untouched.
 */
export function initFromTemplate(template: AlertTemplate, units: UnitSettings): AlarmDraft {
  const modeUniverse = [...RECIPE_MODE_UNIVERSE];
  // The recipe's defaults decide what starts CHECKED, not what is offered. A
  // recipe whose defaults fold to nothing falls back to "watch in every mode"
  // rather than opening with a form that fails its own "pick a mode" validation.
  const recommended = new Set(modeUnion(template.defaultModes));
  const preChecked = modeUniverse.filter((m) => recommended.has(m));
  // SNAP THE RECOMMENDATION TO THE SENTENCE THE FORM WILL PRINT FOR IT. No
  // recipe's `defaultModes` lists `Sailing` — the registry predates the mode
  // being elected — so the five-mode default reads as "Always" in the watch
  // field while WRITING a set that leaves the alarm disarmed for the whole
  // passage. Detection is deliberately lossy (Sailing folds onto Underway) and
  // must never rewrite a STORED def, but a create draft has nothing stored yet:
  // here, and only here, the honest fix is to write the full set the sentence
  // claims. A recommendation that matches no preset (`low_oil_pressure`'s
  // Underway + Maintenance) opens on "Custom…" with the grid showing, and is
  // carried through verbatim.
  const detected = detectWatchPreset(preChecked);
  const watched = detected === 'custom' ? preChecked : watchPresetModes(detected);
  const values: Record<string, Record<string, string>> = {};
  for (const mode of modeUniverse) {
    values[mode] = {};
    for (const p of template.params) values[mode][p.key] = '';
  }
  const support = conditionSupport(template);
  return {
    recipeId: template.recipeId,
    title: '',
    severity: template.defaultSeverity,
    modeUniverse,
    enabledModes: watched.length > 0 ? watched : [...modeUniverse],
    values,
    baseline: {},
    units,
    params: [...template.params],
    // One number per param until the operator asks for more.
    splitModes: [],
    ...(support !== undefined ? { conditionSupport: support } : {}),
    // OFF by default: a new alarm watches all the time until the operator says
    // otherwise. A condition can only ever make an alarm quieter, so it is never
    // the safe default to turn on for someone.
    engineCondition: false,
    minRpm: String(support?.defaultMinRpm ?? DEFAULT_ENGINE_RUNNING_RPM),
    otherConditionClauses: [],
    conditionLogic: 'all',
    ...(template.pathFamily !== undefined ? { bindingFamily: template.pathFamily } : {}),
    ...(templateRequiresPin(template) ? { bindingRequired: true as const } : {}),
    // "Any" by default: a new alarm watches whichever instance the boat reports,
    // exactly as every alarm did before pins existed. Pinning is a deliberate
    // narrowing — pre-selecting one instance for someone would quietly leave the
    // others unwatched.
    binding: '',
  };
}

/**
 * The first finite stored value for a param across any mode — the shared value —
 * as the text of its input box beside the number that text renders, or
 * `undefined` when no mode stores one.
 *
 * ONE PAIR FOR EVERY MODE, deliberately. A shared param shows one box, so every
 * mode's baseline is that box's text and the number it came from. Pairing a
 * mode's own stored number with another mode's text would be the one genuinely
 * dangerous shape here: {@link storedNumber} would answer a number the box never
 * showed. The only definition where that could arise is a `perMode: false` param
 * stored with different numbers per mode, and collapsing those onto one is what
 * this form has always done.
 */
function storedSharedBaseline(
  def: AlarmDefView,
  param: RecipeParamMeta,
  units: UnitSettings
): ParamBaseline | undefined {
  for (const block of Object.values(def.thresholdsByMode)) {
    const raw = block?.[param.key];
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return { text: paramValueText(param, raw, units), stored: raw };
    }
  }
  return undefined;
}

/**
 * Whether a stored param carries two or more DIFFERENT numbers across the
 * definition's modes — the only case where collapsing it to one shared box
 * would lose something.
 *
 * A mode with no block, or a block missing this key, is a GAP rather than a
 * difference: an alarm stored with one number under `Anchored` and nothing
 * elsewhere means one number, and opening it broken-out would present four
 * empty boxes as though the operator had left work unfinished.
 */
function storedValuesDiffer(def: AlarmDefView, key: string): boolean {
  const seen = new Set<number>();
  for (const block of Object.values(def.thresholdsByMode)) {
    const raw = block?.[key];
    if (typeof raw === 'number' && Number.isFinite(raw)) seen.add(raw);
    if (seen.size > 1) return true;
  }
  return false;
}

/**
 * An EDIT draft from an existing definition, prefilled with its stored values.
 *
 * `units` is REQUIRED (see {@link initFromTemplate}) and does two things here:
 * it decides what the boxes READ, and — through the baseline captured beside
 * every filled box — it is what makes an untouched save emit the original stored
 * numbers bit for bit.
 */
export function initFromDef(
  def: AlarmDefView,
  template: AlertTemplate,
  units: UnitSettings
): AlarmDraft {
  const modeUniverse = [...RECIPE_MODE_UNIVERSE];
  // A non-perMode param carries ONE shared value; fan it out to every universe
  // mode so a mode the def never listed (but the operator can now enable)
  // inherits it instead of surfacing a spurious blank.
  // Which params open BROKEN OUT: only the ones whose stored numbers actually
  // differ between modes. An alarm someone deliberately gave a lower floor at
  // anchor keeps showing that; every other alarm opens with one box per param,
  // which is what it has always meant.
  const splitModes = template.params
    .filter((p) => p.perMode && storedValuesDiffer(def, p.key))
    .map((p) => p.key);
  const split = new Set(splitModes);
  const sharedByKey: Record<string, ParamBaseline | undefined> = {};
  for (const p of template.params) {
    if (!split.has(p.key)) sharedByKey[p.key] = storedSharedBaseline(def, p, units);
  }
  const values: Record<string, Record<string, string>> = {};
  // Captured beside the boxes, never derived from them afterwards: this is the
  // record that lets an untouched save re-emit the definition's own numbers.
  const baseline: Record<string, Record<string, ParamBaseline>> = {};
  for (const mode of modeUniverse) {
    values[mode] = {};
    baseline[mode] = {};
    for (const p of template.params) {
      // A shared param fans its one stored number out to every universe mode, so
      // a mode the def never listed (but the operator can now enable) inherits
      // it instead of surfacing a spurious blank.
      if (!split.has(p.key)) {
        const shared = sharedByKey[p.key];
        values[mode][p.key] = shared?.text ?? '';
        if (shared !== undefined && shared.text.length > 0) baseline[mode][p.key] = shared;
        continue;
      }
      // Stored → the unit of the box. A registry `display` unit picks a
      // rendering that converts back to this exact stored number; an account
      // unit rounds to its display precision and does not, which is what the
      // baseline recorded here exists to cover.
      const raw = blockForMode(def.thresholdsByMode, mode)?.[p.key];
      if (typeof raw === 'number' && Number.isFinite(raw)) {
        const text = paramValueText(p, raw, units);
        values[mode][p.key] = text;
        if (text.length > 0) baseline[mode][p.key] = { text, stored: raw };
      } else {
        values[mode][p.key] = '';
      }
    }
  }
  // Case-insensitive membership: the universe carries the canonical spelling, so
  // a def stored with the FTUE's lowercase modes must still read as enabled
  // (an exact-match filter would leave every box unchecked).
  const enabledLower = new Set(def.enabledModes.map((m) => m.toLowerCase()));
  const support = conditionSupport(template);
  // Stored conditions, read defensively: the engine preset (if any) drives the
  // toggle, and every OTHER clause is carried through untouched.
  //
  // The preset becomes the TOGGLE only when the library declares this recipe
  // supports it. Without that metadata — a def synced from a boat running a
  // newer library, or a `supportsCondition` block that degraded to `undefined`
  // — the form cannot author the preset, so adopting it into the toggle would
  // DELETE it on save: `draftConditions` emits the preset only when
  // `conditionSupport` is set. Leaving it in `otherConditionClauses` carries it
  // through verbatim, exactly like any other clause this form does not author.
  const stored = readConditions(def.conditions);
  const clauses = stored?.clauses ?? [];
  const presetIndex = support === undefined ? -1 : clauses.findIndex((c) => 'preset' in c);
  const preset = presetIndex === -1 ? undefined : clauses[presetIndex];
  const presetMinRpm =
    preset !== undefined && 'preset' in preset && typeof preset.minRpm === 'number'
      ? preset.minRpm
      : undefined;
  return {
    recipeId: def.recipeId,
    title: def.title,
    severity: def.severity ?? template.defaultSeverity,
    modeUniverse,
    enabledModes: modeUniverse.filter((m) => enabledLower.has(m.toLowerCase())),
    values,
    baseline,
    units,
    params: [...template.params],
    splitModes,
    ...(support !== undefined ? { conditionSupport: support } : {}),
    engineCondition: preset !== undefined,
    // A stored preset with no `minRpm` runs on the recipe's own default, so the
    // field shows that number rather than a blank the operator must guess at.
    minRpm: String(presetMinRpm ?? support?.defaultMinRpm ?? DEFAULT_ENGINE_RUNNING_RPM),
    otherConditionClauses: clauses.filter((_, i) => i !== presetIndex),
    conditionLogic: stored?.logic === 'any' ? 'any' : 'all',
    binding: readBindingPath(def) ?? '',
    ...(template.pathFamily !== undefined ? { bindingFamily: template.pathFamily } : {}),
    // Carried on the EDIT draft too, so clearing the pin off a stored bilge
    // alarm is refused here rather than by the PATCH — the form must not offer
    // an "Automatic" option the server will reject.
    ...(templateRequiresPin(template) ? { bindingRequired: true as const } : {}),
  };
}

// --- field actions (pure; each returns a new draft) ------------------------

export function setTitle(draft: AlarmDraft, title: string): AlarmDraft {
  return { ...draft, title };
}

export function setSeverity(draft: AlarmDraft, severity: AlarmSeverity): AlarmDraft {
  return { ...draft, severity };
}

/**
 * Toggle a mode on/off, keeping `enabledModes` in universe order.
 *
 * The tick itself is {@link toggleWatchGridMode}, shared with the custom
 * builder: it carries `Sailing` with `Underway` exactly as the presets do, which
 * is what keeps the grid able to un-write everything a preset writes even though
 * `Sailing` has no checkbox of its own.
 */
export function toggleMode(draft: AlarmDraft, mode: string): AlarmDraft {
  if (!draft.modeUniverse.includes(mode)) return draft;
  const next = toggleWatchGridMode(draft.enabledModes, mode);
  return { ...draft, enabledModes: draft.modeUniverse.filter((m) => next.has(m)) };
}

/**
 * Replace the whole mode set at once — what choosing a watch-schedule preset
 * does (`watchSchedule.ts`). Same normalization as {@link toggleMode}: universe
 * order, deduped, and anything outside the universe dropped. A preset can never
 * write a mode the checkbox grid could not un-write: the one mode the grid does
 * not offer is `Sailing`, and {@link toggleMode} takes that off with `Underway`.
 */
export function setEnabledModes(draft: AlarmDraft, modes: readonly string[]): AlarmDraft {
  const wanted = new Set(modes);
  return { ...draft, enabledModes: draft.modeUniverse.filter((m) => wanted.has(m)) };
}

/** Whether a param is currently broken out per mode (see {@link AlarmDraft.splitModes}). */
export function isSplitByMode(draft: AlarmDraft, key: string): boolean {
  return draft.splitModes.includes(key);
}

/**
 * Whether a param COULD be broken out per mode — the recipe metadata's
 * `perMode` flag, which is a permission rather than an instruction. A
 * `perMode: false` param has one meaning for the whole alarm and gets no toggle.
 */
export function canSplitByMode(draft: AlarmDraft, key: string): boolean {
  return draft.params.find((p) => p.key === key)?.perMode === true;
}

/**
 * Set one threshold value. A SHARED param (the default) fans the value out to
 * every mode in the universe — the API stores thresholds per mode either way, so
 * "one number" is one number written everywhere. A param the operator has broken
 * out sets only the given mode.
 */
export function setThreshold(
  draft: AlarmDraft,
  mode: string,
  key: string,
  value: string
): AlarmDraft {
  const param = draft.params.find((p) => p.key === key);
  if (param === undefined) return draft;
  const values: Record<string, Record<string, string>> = {};
  for (const m of draft.modeUniverse) values[m] = { ...(draft.values[m] ?? {}) };
  if (param.perMode && isSplitByMode(draft, key)) {
    const block = values[mode] ?? {};
    block[key] = value;
    values[mode] = block;
  } else {
    for (const m of draft.modeUniverse) {
      const block = values[m] ?? {};
      block[key] = value;
      values[m] = block;
    }
  }
  return { ...draft, values };
}

/**
 * Break a param out per mode, or collapse it back to one shared number.
 *
 * Turning it ON seeds every mode with the shared number that was there, so the
 * operator edits away from a known state rather than filling blanks.
 *
 * Turning it OFF is the destructive direction and is deliberately simple: the
 * FIRST enabled mode's number wins and is fanned everywhere — its TEXT and its
 * BASELINE together, see {@link fanBaseline}. The form warns before offering
 * this when the numbers differ — see {@link splitCollapseLoses}.
 *
 * A no-op on a param the recipe does not allow to vary.
 */
export function setSplitByMode(draft: AlarmDraft, key: string, on: boolean): AlarmDraft {
  if (!canSplitByMode(draft, key)) return draft;
  if (on === isSplitByMode(draft, key)) return draft;
  if (on) {
    // Values are already identical across modes (that is what shared means), so
    // only the flag moves — and so are their baselines, for the same reason.
    return { ...draft, splitModes: [...draft.splitModes, key] };
  }
  // Clear the flag FIRST: `setThreshold` reads it to decide whether to write one
  // mode or fan out, so collapsing through a draft that still says "split" would
  // write the surviving number to the mode named here and leave every other mode
  // holding its old one — the exact loss this is supposed to make deliberate.
  //
  // The surviving mode is the one {@link sharedValue} reads, named here so the
  // text and the baseline that get fanned are provably the same box's.
  const keepMode = draft.enabledModes[0] ?? draft.modeUniverse[0];
  const keep = sharedValue(draft, key);
  const cleared: AlarmDraft = {
    ...draft,
    splitModes: draft.splitModes.filter((k) => k !== key),
  };
  return fanBaseline(setThreshold(cleared, keepMode ?? '', key, keep), key, keepMode);
}

/**
 * Fan the surviving mode's BASELINE out beside the text {@link setThreshold}
 * just fanned, so a collapsed param resolves to ONE stored number everywhere.
 *
 * Without this the collapse writes the survivor's text into every mode and
 * leaves each mode holding the baseline of the number it used to have. The two
 * still MATCH — an imperial account editing `shallow_depth` split 20 m /
 * 22 m sees "66" and "72", and after collapsing to "66" the Anchored box holds
 * "66" against a baseline whose text is "72"… but round-trip precision makes
 * the near misses worse than the obvious ones: wherever the fanned text equals
 * another mode's baseline text, {@link storedNumber} answers with THAT mode's
 * original stored number. One visible box then emits
 * `{"Underway":20,"Anchored":20.1168}` — 66 ft re-converted — and
 * {@link perModeEvaluationNotice} reports a divergence the owner cannot see or
 * reach.
 *
 * A survivor with NO baseline (a box nothing filled) REMOVES the others rather
 * than leaving them: a stale baseline there would let a coincidental retype
 * resurrect a stored number that is no longer on screen anywhere.
 */
function fanBaseline(draft: AlarmDraft, key: string, keepMode: string | undefined): AlarmDraft {
  if (draft.baseline === undefined) return draft;
  const keep = keepMode === undefined ? undefined : draft.baseline[keepMode]?.[key];
  const baseline: Record<string, Record<string, ParamBaseline>> = {};
  for (const [mode, block] of Object.entries(draft.baseline)) {
    const next: Record<string, ParamBaseline> = {};
    for (const [k, entry] of Object.entries(block)) if (k !== key) next[k] = entry;
    if (keep !== undefined) next[key] = keep;
    baseline[mode] = next;
  }
  // A universe mode the baseline record never carried still needs the entry:
  // `setThreshold` filled its box, so an untouched save has to resolve it the
  // same way as every other box holding that same text.
  if (keep !== undefined) {
    for (const mode of draft.modeUniverse) baseline[mode] ??= { [key]: keep };
  }
  return { ...draft, baseline };
}

/**
 * The values that would be DISCARDED by collapsing a broken-out param back to
 * one number, or `null` when nothing would be. Lets the form ask before losing
 * an operator's deliberate per-mode numbers instead of dropping them on a click.
 */
export function splitCollapseLoses(draft: AlarmDraft, key: string): string[] | null {
  if (!isSplitByMode(draft, key)) return null;
  const keep = sharedValue(draft, key).trim();
  const lost = draft.enabledModes
    .map((m) => modeValue(draft, m, key).trim())
    .filter((v) => v.length > 0 && v !== keep);
  return lost.length > 0 ? [...new Set(lost)] : null;
}

/** The shared (collapsed) input value — read from the first enabled mode. */
export function sharedValue(draft: AlarmDraft, key: string): string {
  const mode = draft.enabledModes[0] ?? draft.modeUniverse[0];
  return (mode !== undefined ? draft.values[mode]?.[key] : undefined) ?? '';
}

/** One mode's raw value for a param key. */
export function modeValue(draft: AlarmDraft, mode: string, key: string): string {
  return draft.values[mode]?.[key] ?? '';
}

/**
 * Attach / detach the engine-running preset. A no-op on a recipe that does not
 * take one — the API rejects the preset there, so the draft must not hold it
 * even if a caller asks.
 */
export function setEngineCondition(draft: AlarmDraft, on: boolean): AlarmDraft {
  if (draft.conditionSupport === undefined) return draft;
  return { ...draft, engineCondition: on };
}

/** Set the RPM floor (raw string; parsed on validate/submit). */
export function setMinRpm(draft: AlarmDraft, minRpm: string): AlarmDraft {
  return { ...draft, minRpm };
}

/** Pin the alarm to one concrete path, or `''` for "any" (family first-match). */
export function setBinding(draft: AlarmDraft, path: string): AlarmDraft {
  return { ...draft, binding: path.trim() };
}

// --- which instance this alarm watches (the binding pin) -------------------

/**
 * Why a stored pin cannot be bound — the two ways the compiler refuses one:
 *
 * - `not-reported`: the boat has stopped reporting that path.
 * - `not-a-match`: the path is reported, but it is not in this recipe's family
 *   (a pin written by another surface, or left behind by a different recipe).
 *
 * They are told apart deliberately. Both leave the alarm compile-pending, but
 * "your boat isn't sending this" and "this isn't something this alarm can
 * watch" call for different fixes, and labelling the second as the first is a
 * claim the operator can see is false on the Instruments page.
 */
export type BindingProblem = 'not-reported' | 'not-a-match';

/** One choosable instance in the "Which one should it watch?" select. */
export interface BindingChoice {
  /** The concrete catalog path this option pins. */
  readonly path: string;
  /** What the option reads on screen (human name, device, honesty flag). */
  readonly label: string;
  /**
   * Absent on an ordinary option. Set only on a STORED pin the compiler cannot
   * bind today — the option is kept, and flagged, rather than dropped.
   */
  readonly problem?: BindingProblem;
  /**
   * `true` when this option's own LABEL reads like the thing the recipe watches
   * — "Aft Bilge High Water" under `bilge_high` (see {@link likelyInstanceHint}).
   *
   * A HINT AND A SORT ORDER, never a filter. Every path in the family stays in
   * the list whether it matches or not, because the guess is made from a name an
   * installer typed and the owner is the only one who knows what is actually
   * wired to channel 4.
   */
  readonly likely?: true;
}

/**
 * How to guess, and how to say so, for a recipe whose instance choice is not in
 * the path — {@link likelyInstanceHint}.
 */
export interface LikelyInstanceHint {
  /** True when this catalog entry's own label reads like the right instance. */
  readonly matches: (signal: Signal) => boolean;
  /** Heading over the guesses. */
  readonly likelyGroup: string;
  /** Heading over everything else in the family. NOT "hidden" — second. */
  readonly otherGroup: string;
}

/**
 * The bilge float-switch label vocabulary, for the ONE recipe whose instance
 * cannot be read off the path (core ADR-116 part A).
 *
 * A DELIBERATE SECOND COPY of core's `bilge_high` channel-label regex
 * (`core/src/setup/recommendations/library.ts`, itself built from
 * `signal-roles.ts` `HIGH_WATER_KEYWORDS`). Core's copy is authoritative and
 * decides which channels the BOAT generates an alarm for; this one only decides
 * which options sort first and carry a hint, and it hides nothing. So the two
 * drifting costs an ordering, never a missing choice or a wrong binding — which
 * is why duplicating four words was preferred to dragging core's role
 * vocabulary into `packages/rules` for a sort key (the alternative the ADR
 * weighed and rejected).
 */
const BILGE_CHANNEL_LABEL =
  /bilge.*(?:high|float|alarm)|high.*bilge|high[\s_-]?water|hi[\s_-]?water|float\s*switch|flood(?![\s_-]?(?:light|lamp))/i;

/**
 * The label guess for a recipe, or `undefined` when the path already says which
 * instance is which (every other recipe: a tank path names its tank).
 *
 * Keyed on the recipe id rather than on `requiresPin`, because the two answer
 * different questions: `requiresPin` says the owner MUST choose, this says we
 * have something useful to suggest. A future pin-mandatory recipe with no
 * vocabulary gets an unsorted, unhinted list rather than a wrong guess.
 */
export function likelyInstanceHint(recipeId: string): LikelyInstanceHint | undefined {
  const base = recipeId.split('#')[0];
  if (base === 'smoke_co_gas') {
    return {
      matches: (signal) => SMOKE_CO_GAS_DETECTOR_LABEL.test(signal.channelLabel?.trim() ?? ''),
      likelyGroup: 'Looks like a smoke, CO or gas detector',
      otherGroup: 'Every other switch channel',
    };
  }
  if (base === 'hatch_security') {
    return {
      matches: (signal) => HATCH_SECURITY_LABEL.test(signal.channelLabel?.trim() ?? ''),
      likelyGroup: 'Looks like a hatch, door or security sensor',
      otherGroup: 'Every other switch channel',
    };
  }
  if (base === 'bilge_pump_cycling') {
    return {
      matches: (signal) => BILGE_PUMP_CHANNEL_LABEL.test(signal.channelLabel?.trim() ?? ''),
      likelyGroup: 'Looks like a bilge pump',
      otherGroup: 'Every other switch channel',
    };
  }
  if (base !== 'bilge_high') return undefined;
  return {
    matches: (signal) => BILGE_CHANNEL_LABEL.test(signal.channelLabel?.trim() ?? ''),
    likelyGroup: 'Looks like a bilge switch',
    otherGroup: 'Every other switch channel',
  };
}

/**
 * The bilge PUMP label vocabulary for `bilge_pump_cycling` (TOM-101) — the same
 * deliberate second copy as {@link BILGE_CHANNEL_LABEL}, of core's
 * `bilge_pump_cycling` channel-label regex. It only sorts and hints.
 */
const BILGE_PUMP_CHANNEL_LABEL = /bilge.*pump|pump.*bilge/i;

/**
 * The two answers for a detector-contact alarm's polarity (`alarmState`,
 * TOM-136, core ADR-217), offered as a choice rather than a 0–1 number box.
 * `value` is the stored number as the draft holds it.
 */
export const CONTACT_STATE_CHOICES: readonly { readonly value: string; readonly label: string }[] =
  [
    { value: '1', label: 'Reads ON when tripped (normally open)' },
    { value: '0', label: 'Reads OFF when tripped (normally closed)' },
  ];

/** Whether a param is the detector-contact polarity, edited as a choice. */
export { isContactStateParam };

/** The refusal for a blank that would clear a stored polarity. */
export const CONTACT_STATE_KEEP =
  'This alarm already knows how its channel is wired. Choose ON or OFF; clearing it would stop the alarm.';

/**
 * Whether the polarity may be left (or set) blank: only while the alarm has
 * never had one. Once a stored definition carries `alarmState`, a blank would
 * disarm it, so the editors offer only the two answers.
 */
export function contactStateClearable(draft: AlarmDraft): boolean {
  for (const block of Object.values(draft.baseline ?? {})) {
    for (const key of Object.keys(block)) if (isContactStateParam(key)) return false;
  }
  return true;
}

/** What a pin-mandatory recipe's channel picker says. */
export interface PinCopy {
  /** The question over the select. */
  readonly question: string;
  /** The note under it, saying why we have to ask. */
  readonly explainer: string;
  /** The error when the owner saves without answering. */
  readonly missing: string;
}

const FLOAT_PIN_COPY: PinCopy = {
  question: 'Which switch channel is the bilge float?',
  explainer:
    'Every switch on your boat looks the same to us — the wiring is what makes one of them the bilge float. We’ve put the channels whose names suggest a bilge first, but they are only names: pick the one it’s actually wired to. If that channel stops reporting, the alarm waits for it rather than quietly switching to another.',
  missing: 'Choose the switch channel your bilge float switch is wired to.',
};

const PUMP_PIN_COPY: PinCopy = {
  question: 'Which switch channel shows the bilge pump running?',
  explainer:
    'Every switch on your boat looks the same to us — the wiring is what makes one of them the bilge pump. We’ve put the channels whose names suggest a bilge pump first, but they are only names: pick the one that shows the pump running, not its high-water float. If that channel stops reporting, the alarm waits for it rather than quietly switching to another.',
  missing: 'Choose the switch channel that shows your bilge pump running.',
};

const SMOKE_PIN_COPY: PinCopy = {
  question: 'Which switch channel is the smoke, CO or gas detector?',
  explainer:
    'Every switch on your boat looks the same to us — the wiring is what makes one of them the detector’s alarm contact. We’ve put the channels whose names suggest a detector first, but they are only names: pick the one it’s actually wired to. If that channel stops reporting, the alarm waits for it rather than quietly switching to another.',
  missing: 'Choose the switch channel your smoke, CO or gas detector is wired to.',
};

const HATCH_PIN_COPY: PinCopy = {
  question: 'Which switch channel is the hatch, door or security sensor?',
  explainer:
    'Every switch on your boat looks the same to us — the wiring is what makes one of them the sensor. We’ve put the channels whose names suggest a hatch, door or security sensor first, but they are only names: pick the one it’s actually wired to. If that channel stops reporting, the alarm waits for it rather than quietly switching to another.',
  missing: 'Choose the switch channel your hatch, door or security sensor is wired to.',
};

/**
 * The words a pin-mandatory recipe asks with. The pin-mandatory recipes share
 * one family and want different channels: the float for high water, the pump's
 * run signal for cycling (TOM-101), a detector's alarm contact for smoke/CO/gas
 * and a hatch or door sensor for security (TOM-136). A cycling alarm pinned to
 * the float would count float trips as pump starts, so the question has to say
 * which.
 */
export function pinCopyFor(recipeId: string): PinCopy {
  switch (recipeId.split('#')[0]) {
    case 'bilge_pump_cycling':
      return PUMP_PIN_COPY;
    case 'smoke_co_gas':
      return SMOKE_PIN_COPY;
    case 'hatch_security':
      return HATCH_PIN_COPY;
    default:
      return FLOAT_PIN_COPY;
  }
}

/** The wording for a pinned path the boat is no longer reporting. */
export const NOT_REPORTED_SUFFIX = ' (not currently reported)';

/** The wording for a pinned path outside this recipe's family. */
export const NOT_A_MATCH_SUFFIX = ' (not something this alarm can watch)';

/**
 * The instances this alarm could watch — the options for the binding select —
 * or an EMPTY list when the question should not be asked at all.
 *
 * Asked when there is a real choice: two or more reported paths in the recipe's
 * family (two engines, a fridge and a freezer), or an alarm that already
 * carries a pin (so the pin is always visible, and always clearable). One
 * reported path and no pin renders nothing — a select with a single option is
 * clutter, and "Automatic" already means that path.
 *
 * PIN-MISS HONESTY: a stored pin the compiler cannot bind is kept as an option,
 * flagged (see {@link BindingProblem}), rather than dropped. Dropping it would
 * snap the control to "Automatic" and state — on the screen that saves the
 * alarm — that it watches any instance, when the compiler is in fact holding it
 * pending on the pinned path. The operator must see the pin to decide whether
 * to clear it.
 *
 * `matches` is the compiler's OWN family predicate (see `pathFamilyMatcher`);
 * no family metadata ⇒ no options, because this build cannot tell which paths
 * the compiler would accept and must not guess.
 *
 * `required` is set for a recipe that cannot run unpinned
 * ({@link AlarmDraft.bindingRequired}). It changes ONE thing: a single-member
 * family is still offered. The usual "one option is clutter" shortcut assumes
 * "Automatic" already means that path, and for a pin-mandatory recipe there is
 * no Automatic — hiding the only option would leave a form that cannot be
 * submitted and does not say why.
 *
 * `showSingle` keeps a one-member family visible when a surface explicitly
 * offers source selection, including the inline alarm editor. It does not
 * make the binding required or remove the Automatic choice.
 *
 * `hint` sorts the LIKELY instances first and marks them
 * ({@link likelyInstanceHint}). It never removes anything: on a boat whose
 * installer labelled nothing, every switch channel is unlabelled, unhinted, and
 * still choosable — the owner knows which one is the bilge even when we do not.
 * That is ADR 0157's rule (narrow within the accepted set; never hide part of
 * it) applied to an ordering.
 */
export function bindingChoices(
  signals: readonly Signal[],
  matches: ((path: string) => boolean) | undefined,
  pinnedPath: string,
  opts: { required?: boolean; showSingle?: boolean; hint?: LikelyInstanceHint } = {}
): BindingChoice[] {
  const pin = pinnedPath.trim();
  if (matches === undefined) return [];
  const inFamily = signals.filter((s) => typeof s.path === 'string' && matches(s.path));
  const hint = opts.hint;
  const choices: BindingChoice[] = inFamily
    .map((s) => {
      const likely = hint !== undefined && hint.matches(s);
      return {
        path: s.path,
        label: bindingLabel(s),
        ...(likely ? { likely: true as const } : {}),
      };
    })
    // Likely first, then by path. A stable secondary key matters: switch
    // channels sort into wiring order, which is how an owner reads a panel.
    .sort(
      (a, b) =>
        Number(b.likely ?? false) - Number(a.likely ?? false) || a.path.localeCompare(b.path)
    );
  if (pin.length > 0 && !choices.some((c) => c.path === pin)) {
    // The pin is reported but outside the family, or not reported at all — two
    // different problems with two different fixes.
    const reported = signals.find((s) => s.path === pin);
    const problem: BindingProblem = reported === undefined ? 'not-reported' : 'not-a-match';
    const suffix = problem === 'not-reported' ? NOT_REPORTED_SUFFIX : NOT_A_MATCH_SUFFIX;
    // Sorted-first so the selected option is the one the eye lands on.
    choices.unshift({
      path: pin,
      label: `${bindingLabel(reported ?? { path: pin, deviceName: null, channelLabel: null })}${suffix}`,
      problem,
    });
  }
  if (choices.length > 1) return choices;
  if (opts.required === true || opts.showSingle === true) return choices;
  return pin.length > 0 ? choices : [];
}

/**
 * "Port engine oil pressure (Yanmar 4JH)" — human name, device when known.
 *
 * Takes the SIGNAL, not a path plus a device name, because a switch channel's
 * real name lives in `channelLabel` ("Aft Bilge High Water") and composing it
 * positionally instead produces "Switch bank 1 channel 4" — the exact failure
 * `signalDisplayName` was introduced to stop happening at a fifth call site.
 * This select is where it would have mattered most: picking the bilge float out
 * of eight numbered channels is impossible without the installer's own words.
 */
function bindingLabel(
  signal: Pick<Signal, 'path' | 'deviceName'> & {
    channelLabel?: string | null;
    displayLabel?: string;
    entityName?: string;
  }
): string {
  const name =
    signal.displayLabel ??
    signalLabel(signal.path, {
      entityName: signal.entityName,
      deviceName: signal.deviceName,
      channelLabel: signal.channelLabel,
    });
  const device = signal.deviceName?.trim() ?? '';
  return device.length > 0 && device !== name ? `${name} (${device})` : name;
}

/**
 * Whether the engine-running preset is a NECESSARY condition for this alarm to
 * fire. The editor's "the alarm fires only while your boat reports engine RPM
 * above that number" is a necessity claim, so it is honest exactly here.
 *
 * Under an `all` fold every clause has to hold, so the claim stands whatever
 * else is stored. Under `any` it stands only while the preset is the sole
 * clause — a one-clause fold means the same either way. Beside another clause
 * an `any` alarm can fire on that one instead, and the exclusive wording would
 * be false.
 */
export function engineConditionIsRequired(draft: AlarmDraft): boolean {
  return draft.conditionLogic === 'all' || draft.otherConditionClauses.length === 0;
}

// --- validation ------------------------------------------------------------

export interface DraftErrors {
  title?: string;
  modes?: string;
  /**
   * The mandatory instance pin, unanswered. Only ever set for a recipe that
   * cannot run unpinned ({@link AlarmDraft.bindingRequired}) — the API refuses
   * that create with `422 invalid_binding`, and catching it here is what turns a
   * server error into a question beside the field that answers it.
   */
  binding?: string;
  /** Per-input threshold errors, keyed `${mode}::${key}` (perMode) or `shared::${key}`. */
  thresholds: Record<string, string>;
  /** The engine-running RPM floor. */
  minRpm?: string;
  /** The condition section as a whole (today: the v1 four-clause cap). */
  conditions?: string;
}

export interface ValidateOpts {
  /** Require a non-empty title (edit mode; create inherits the recipe title). */
  requireTitle?: boolean;
}

/**
 * One box's contents parsed back into the STORED domain, or `undefined` when it
 * is blank or not a number. The single place a draft string becomes a number the
 * API would accept — validation, the create/patch bodies and the per-mode
 * evaluation notice all go through it, so none of them can disagree about what
 * the owner typed.
 *
 * THE BASELINE GUARD (ADR 0189 §4; the note at the top of this file has the
 * why). Three answers, in order:
 *
 *   a. The box still holds the text it opened with ⇒ the ORIGINAL stored number,
 *      verbatim. No conversion runs at all, so no float artefact can exist.
 *   b. The text changed but means the same number ("32.80" for "32.8", a
 *      retype, a stray space) ⇒ the original stored number again. An owner who
 *      typed the same value back has not edited anything.
 *   c. Otherwise ⇒ converted exactly once, through the same resolver the box
 *      was rendered with.
 *
 * (b) is compared against the BASELINE TEXT, not against a re-conversion of the
 * baseline number: the point is "is this still the number that was on screen",
 * and the screen only ever showed the text.
 */
function storedNumber(
  param: RecipeParamMeta,
  raw: string | undefined,
  baseline?: ParamBaseline,
  units?: UnitSettings
): number | undefined {
  const trimmed = (raw ?? '').trim();
  if (trimmed.length === 0) return undefined;
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return undefined;
  if (baseline !== undefined) {
    if (trimmed === baseline.text) return baseline.stored;
    const original = Number(baseline.text);
    if (Number.isFinite(original) && original === n) return baseline.stored;
  }
  return paramEditorUnits(param, units).toStored(n);
}

/**
 * What one box opened holding, or `undefined` for a box nothing filled. The ONE
 * lookup every {@link storedNumber} caller uses, so no path can accidentally
 * resolve a box without its baseline — a notice computed without one would
 * disagree with the wire body about a field the owner never touched.
 */
function baselineFor(
  draft: AlarmDraft,
  mode: string | undefined,
  key: string
): ParamBaseline | undefined {
  if (mode === undefined) return undefined;
  return draft.baseline?.[mode]?.[key];
}

/**
 * Bounds are checked in the STORED domain — that is what the API validates, and
 * a duration is rounded to whole milliseconds on the way there, so 3600.0004
 * seconds is a legal 3 600 000 ms and must not be refused. They are QUOTED in
 * the edited one: telling someone who typed seconds that the limit is 3600000
 * is the jargon this whole change removes.
 */
function validateValue(
  raw: string | undefined,
  param: RecipeParamMeta,
  baseline: ParamBaseline | undefined,
  settings: UnitSettings | undefined
): string | undefined {
  const trimmed = (raw ?? '').trim();
  // A choice, not a number, and an OPTIONAL one for a new alarm (third review of
  // PR 710): left unanswered, the alarm saves and waits pending, asking. Only an
  // answer other than the two is refused (the API refuses it too). A blank
  // that would CLEAR a stored answer is refused in `validate` below.
  if (isContactStateParam(param.key)) {
    return trimmed === '' || trimmed === '0' || trimmed === '1'
      ? undefined
      : 'Choose whether the channel reads ON or OFF when it trips.';
  }
  if (trimmed.length === 0) return 'Required.';
  if (!Number.isFinite(Number(trimmed))) return 'Enter a number.';
  // Through the same resolver as the body builder, baseline and all: a stored
  // value just outside a bound must not be refused on a save that never touched
  // it, and the number checked here has to be the number that would be sent.
  const stored = storedNumber(param, trimmed, baseline, settings);
  if (stored === undefined) return 'Enter a number.';
  const units = paramEditorUnits(param, settings);
  const suffix = units.unitSuffix.length > 0 ? ` ${units.unitSuffix}` : '';
  if (param.min !== undefined && stored < param.min) {
    return `Must be at least ${formatEditedNumber(units.min ?? param.min)}${suffix}.`;
  }
  if (param.max !== undefined && stored > param.max) {
    return `Must be at most ${formatEditedNumber(units.max ?? param.max)}${suffix}.`;
  }
  return undefined;
}

/** Per-field errors for the draft. Empty (via {@link hasErrors}) ⇒ submittable. */
export function validate(draft: AlarmDraft, opts: ValidateOpts = {}): DraftErrors {
  const errors: DraftErrors = { thresholds: {} };
  // The mandatory pin (core ADR-116 part A). Asked as a question rather than
  // reported as a server error, and worded as the thing we cannot know: the
  // path says a switch channel tripped, only the wiring says which one is the
  // bilge.
  if (draft.bindingRequired === true && draft.binding.trim().length === 0) {
    errors.binding = pinCopyFor(draft.recipeId).missing;
  }
  if (opts.requireTitle === true && draft.title.trim().length === 0) {
    errors.title = 'Give this alarm a name.';
  }
  if (draft.enabledModes.length === 0) {
    errors.modes = 'Choose at least one mode to watch.';
  }
  for (const param of draft.params) {
    // Errors are keyed to the boxes ACTUALLY on screen. A collapsed param has
    // one box and one error; a broken-out one has an error per mode. Validating
    // per mode while rendering one box would block the save with a message the
    // operator has nowhere to read.
    if (param.perMode && isSplitByMode(draft, param.key)) {
      for (const mode of draft.enabledModes) {
        const err = validateValue(
          draft.values[mode]?.[param.key],
          param,
          baselineFor(draft, mode, param.key),
          draft.units
        );
        if (err !== undefined) errors.thresholds[`${mode}::${param.key}`] = err;
      }
    } else {
      const mode = draft.enabledModes[0];
      const err = validateValue(
        mode !== undefined ? draft.values[mode]?.[param.key] : undefined,
        param,
        baselineFor(draft, mode, param.key),
        draft.units
      );
      if (err !== undefined) errors.thresholds[`shared::${param.key}`] = err;
    }
  }
  // A blank polarity must never CLEAR the one an alarm already has (fourth
  // review): that would disarm a life-safety alarm from a form that looked like
  // it was only changing a name. The editors offer no blank choice then; this
  // holds the line for a draft that reached a blank anyway.
  for (const param of draft.params) {
    if (!isContactStateParam(param.key) || contactStateClearable(draft)) continue;
    if (sharedValue(draft, param.key).trim() === '') {
      errors.thresholds[`shared::${param.key}`] = CONTACT_STATE_KEEP;
    }
  }
  if (draft.engineCondition && draft.conditionSupport !== undefined) {
    const raw = draft.minRpm.trim();
    const n = Number(raw);
    if (raw.length === 0) errors.minRpm = 'Required.';
    else if (!Number.isFinite(n)) errors.minRpm = 'Enter a number.';
    // The wire schema takes a POSITIVE minRpm; 0 would mean "running whenever
    // the tach reports", which is not what the checkbox promises.
    else if (n <= 0) errors.minRpm = 'Must be more than 0 RPM.';
    else if (n > MAX_ENGINE_RUNNING_RPM) {
      errors.minRpm = `Must be at most ${MAX_ENGINE_RUNNING_RPM} RPM.`;
    }
    if (draft.otherConditionClauses.length >= MAX_CONDITION_CLAUSES) {
      errors.conditions = `This alarm already carries ${MAX_CONDITION_CLAUSES} conditions — remove one before adding the engine-running condition.`;
    }
  }
  return errors;
}

export function hasErrors(errors: DraftErrors): boolean {
  return (
    errors.title !== undefined ||
    errors.binding !== undefined ||
    errors.modes !== undefined ||
    errors.minRpm !== undefined ||
    errors.conditions !== undefined ||
    Object.keys(errors.thresholds).length > 0
  );
}

// --- body builders ---------------------------------------------------------

/** The wire `conditions` block: how the clauses fold, and the clauses. */
export interface AlarmConditionsBody {
  logic: 'all' | 'any';
  clauses: ConditionClauseSpec[];
}

export interface AlarmCreateBody {
  recipeId: string;
  title?: string;
  severity: AlarmSeverity;
  enabledModes: string[];
  thresholdsByMode: Record<string, Record<string, number>>;
  /** Omitted entirely when the alarm has no conditions (create takes no null). */
  conditions?: AlarmConditionsBody;
  /** Omitted entirely for "any" — an absent pin keeps the definition digest-identical. */
  binding?: { path: string };
}

export interface AlarmPatchBody {
  title?: string;
  severity?: AlarmSeverity;
  enabledModes?: string[];
  thresholdsByMode?: Record<string, Record<string, number>>;
  /** `null` CLEARS the stored conditions; an object replaces them. */
  conditions?: AlarmConditionsBody | null;
  /** `null` CLEARS the pin (back to "any"); an object re-pins. */
  binding?: { path: string } | null;
}

/**
 * The `conditions` block this draft authors, or `null` when it authors none
 * (the wire schema takes 1..4 clauses, so "no conditions" is an absent block on
 * create and an explicit `null` on patch — never an empty array).
 *
 * The engine preset goes first, then every clause this form does not author,
 * verbatim — including a STORED preset this build cannot render as the toggle
 * (see {@link AlarmDraft.otherConditionClauses}), which rides through here
 * rather than being dropped.
 */
export function draftConditions(draft: AlarmDraft): AlarmConditionsBody | null {
  const clauses: ConditionClauseSpec[] = [];
  if (draft.engineCondition && draft.conditionSupport !== undefined) {
    const minRpm = Number(draft.minRpm.trim());
    clauses.push(
      Number.isFinite(minRpm) && minRpm > 0
        ? { preset: 'engine_running', minRpm }
        : // An unusable number falls back to the recipe's own default rather
          // than sending a value the schema rejects; validate() blocks the save
          // first, so this only guards a programmatic caller.
          { preset: 'engine_running' }
    );
  }
  clauses.push(...draft.otherConditionClauses);
  if (clauses.length === 0) return null;
  return { logic: draft.conditionLogic, clauses };
}

/**
 * One mode's boxes as the numbers that go on the wire — STORED units, so a
 * "60" in a seconds box leaves here as `60000`. An unfilled field is skipped,
 * not coerced (`Number('')` is 0, and 0 is a legal threshold).
 *
 * Every box is resolved WITH ITS BASELINE, which is what makes an untouched key
 * come out `===` the number the definition already holds — the property the
 * all-or-nothing `thresholdsByMode` patch depends on.
 */
function numericValues(draft: AlarmDraft, mode: string): Record<string, number> {
  const vals = draft.values[mode];
  const out: Record<string, number> = {};
  for (const p of draft.params) {
    const stored = storedNumber(p, vals?.[p.key], baselineFor(draft, mode, p.key), draft.units);
    if (stored !== undefined) out[p.key] = stored;
  }
  return out;
}

function buildThresholds(draft: AlarmDraft): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  for (const mode of draft.enabledModes) {
    out[mode] = numericValues(draft, mode);
  }
  return out;
}

/** The full POST body for creating this definition. */
export function toCreateBody(draft: AlarmDraft): AlarmCreateBody {
  const title = draft.title.trim();
  const conditions = draftConditions(draft);
  const binding = draft.binding.trim();
  return {
    recipeId: draft.recipeId,
    ...(title.length > 0 ? { title } : {}),
    severity: draft.severity,
    enabledModes: [...draft.enabledModes],
    thresholdsByMode: buildThresholds(draft),
    ...(conditions !== null ? { conditions } : {}),
    ...(binding.length > 0 ? { binding: { path: binding } } : {}),
  };
}

function sameStringSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const sb = new Set(b);
  return a.every((x) => sb.has(x));
}

function sameThresholds(
  next: Record<string, Record<string, number>>,
  prev: Record<string, Record<string, unknown>>
): boolean {
  const nextModes = Object.keys(next).sort();
  const prevModes = Object.keys(prev).sort();
  if (nextModes.length !== prevModes.length) return false;
  if (nextModes.some((m, i) => m !== prevModes[i])) return false;
  for (const mode of nextModes) {
    const nm = next[mode] ?? {};
    const pm = prev[mode] ?? {};
    const nmKeys = Object.keys(nm).sort();
    const pmNumericKeys = Object.entries(pm)
      .filter(([, v]) => typeof v === 'number')
      .map(([k]) => k)
      .sort();
    if (nmKeys.length !== pmNumericKeys.length) return false;
    if (nmKeys.some((k, i) => k !== pmNumericKeys[i])) return false;
    for (const key of nmKeys) {
      if (nm[key] !== pm[key]) return false;
    }
  }
  return true;
}

/**
 * A clause as one comparable string. Written by hand rather than `JSON.stringify`
 * so key ORDER can never make an unchanged clause look edited (and re-send a
 * whole conditions block, recompiling the boat's ruleset for nothing).
 *
 * A preset with NO `minRpm` runs on `defaultMinRpm`, so it compares equal to one
 * carrying that number — otherwise merely OPENING the editor on such an alarm
 * (the form fills the default into the field) would look like an edit.
 */
function clauseKey(clause: ConditionClauseSpec, defaultMinRpm: number): string {
  if ('preset' in clause) return `preset:${clause.preset}:${clause.minRpm ?? defaultMinRpm}`;
  return `${clause.type}:${clause.path}:${clause.value ?? ''}`;
}

/**
 * Whether two conditions blocks mean the same thing. Clause ORDER does not
 * count — the fold is `all`/`any` either way, so re-ordering is not an edit.
 */
function sameConditions(
  next: AlarmConditionsBody | null,
  prev: AlarmConditionsBody | null,
  defaultMinRpm: number
): boolean {
  if (next === null || prev === null) return next === prev;
  if (next.logic !== prev.logic) return false;
  const a = next.clauses.map((c) => clauseKey(c, defaultMinRpm)).sort();
  const b = prev.clauses.map((c) => clauseKey(c, defaultMinRpm)).sort();
  return a.length === b.length && a.every((k, i) => k === b[i]);
}

/**
 * The PATCH body — CHANGED FIELDS ONLY, diffed against the stored definition.
 *
 * The threshold diff is taken in STORED units on both sides: `buildThresholds`
 * has already converted the boxes back down (seconds → ms, percent → ratio), and
 * `original.thresholdsByMode` is what the API returned. Comparing the edited
 * strings instead would make merely opening an alarm look like an edit the
 * moment a conversion printed a trailing digit, rewriting and re-delivering the
 * boat's whole ruleset for nothing.
 *
 * `thresholdsByMode` is all-or-nothing (the API replaces the whole map), so it
 * is included only when the resulting per-mode numbers actually differ. So is
 * `conditions`: unchecking the toggle on an alarm that had the preset sends an
 * explicit `null` (the API's clear), and an unchanged block sends nothing.
 */
export function toPatchBody(draft: AlarmDraft, original: AlarmDefView): AlarmPatchBody {
  const patch: AlarmPatchBody = {};
  const title = draft.title.trim();
  if (title.length > 0 && title !== original.title) patch.title = title;
  if (draft.severity !== original.severity) patch.severity = draft.severity;
  if (!sameStringSet(draft.enabledModes, original.enabledModes)) {
    patch.enabledModes = [...draft.enabledModes];
  }
  const nextThresholds = buildThresholds(draft);
  if (!sameThresholds(nextThresholds, original.thresholdsByMode)) {
    patch.thresholdsByMode = nextThresholds;
  }
  const nextConditions = draftConditions(draft);
  const stored = readConditions(original.conditions);
  const prevConditions: AlarmConditionsBody | null =
    stored === undefined ? null : { logic: stored.logic ?? 'all', clauses: [...stored.clauses] };
  const defaultMinRpm = draft.conditionSupport?.defaultMinRpm ?? DEFAULT_ENGINE_RUNNING_RPM;
  if (!sameConditions(nextConditions, prevConditions, defaultMinRpm)) {
    patch.conditions = nextConditions;
  }
  // The pin, same all-or-nothing shape as conditions: back to "Automatic" on an
  // alarm that had one sends an explicit `null` (the API's clear), a different
  // instance sends the new object, and an unchanged pin sends nothing — so
  // merely opening the editor never re-pins and never recompiles the ruleset.
  const nextBinding = draft.binding.trim();
  const prevBinding = readBindingPath(original) ?? '';
  if (nextBinding !== prevBinding) {
    patch.binding = nextBinding.length > 0 ? { path: nextBinding } : null;
  }
  return patch;
}

export function isEmptyPatch(patch: AlarmPatchBody): boolean {
  return Object.keys(patch).length === 0;
}

// --- per-mode evaluation disclosure (v1 single-rule collapse) --------------
//
// The shared @neomaris/rules compiler collapses a per-mode definition to ONE
// rule carrying a single setpoint, gated across all enabled modes — so only the
// PRIMARY mode's numbers actually bite; the other modes' saved values do not
// (yet). This MIRRORS the compiler's selection EXACTLY (`packages/rules/src/
// recipes.ts` > `primaryParams`, and the Electron editor's `primaryEvaluatedMode`
// shipped in #369): the first `enabledModes` entry that carries a threshold
// block, else the sorted-first block key. Kept here (pure + tested) so the web
// editor can disclose it honestly.

/** A parsed, complete numeric block per mode — the shape the compiler sees. */
function completeBlocks(draft: AlarmDraft): Record<string, Record<string, number>> {
  const out: Record<string, Record<string, number>> = {};
  if (draft.params.length === 0) return out;
  for (const mode of draft.enabledModes) {
    const raw = draft.values[mode] ?? {};
    const clean: Record<string, number> = {};
    let full = true;
    for (const p of draft.params) {
      // STORED units — this block is compared against what the compiler will
      // actually evaluate, so it has to be in the compiler's domain. Through the
      // SAME resolver, baseline included, as `buildThresholds`: a notice that
      // raw-converted while the body re-emitted the baseline would tell the
      // owner two modes differ when the bytes on the wire say they do not.
      const stored = storedNumber(p, raw[p.key], baselineFor(draft, mode, p.key), draft.units);
      if (stored === undefined) {
        full = false;
        break;
      }
      clean[p.key] = stored;
    }
    if (full) out[mode] = clean;
  }
  return out;
}

/** Shallow numeric-record equality (same keys, same values). */
function sameNumberRecord(a: Record<string, number>, b: Record<string, number>): boolean {
  const aKeys = Object.keys(a);
  if (aKeys.length !== Object.keys(b).length) return false;
  for (const k of aKeys) if (a[k] !== b[k]) return false;
  return true;
}

/**
 * The mode whose threshold values the v1 shared compiler ACTUALLY evaluates: the
 * first `enabledModes` entry that carries a threshold block, else the sorted-first
 * block key. `null` when no mode carries thresholds at all. Mirrors the compiler's
 * `primaryParams` selection exactly.
 */
export function primaryEvaluatedMode(
  enabledModes: readonly string[],
  thresholdsByMode: Record<string, Record<string, number>>
): string | null {
  for (const m of enabledModes) {
    if (thresholdsByMode[m]) return m;
  }
  const keys = Object.keys(thresholdsByMode).sort();
  return keys[0] ?? null;
}

/** The honesty hint for the editor: which mode v1 evaluates + the modes it doesn't. */
export interface PerModeEvaluationNotice {
  /** The primary mode whose values v1 evaluates across all enabled modes. */
  readonly evaluatedMode: string;
  /** The other enabled modes whose distinct values are stored but not yet evaluated. */
  readonly otherModes: string[];
}

/**
 * The honest per-mode disclosure for a DRAFT, or `null` when there is nothing to
 * disclose. It fires only when two-or-more enabled modes carry a COMPLETE block
 * (what the compiler will actually see — a partial block is never sent) AND those
 * blocks are not all identical: only then does the v1 single-rule collapse drop
 * distinct per-mode values. `evaluatedMode` is the compiler's {@link
 * primaryEvaluatedMode} choice over those complete blocks.
 */
export function perModeEvaluationNotice(draft: AlarmDraft): PerModeEvaluationNotice | null {
  const complete = completeBlocks(draft);
  const modes = draft.enabledModes.filter((m) => complete[m] !== undefined);
  const first = modes[0] !== undefined ? complete[modes[0]] : undefined;
  if (modes.length < 2 || first === undefined) return null;
  // Identical blocks lose nothing in the collapse — no need to disclose.
  if (modes.every((m) => sameNumberRecord(complete[m] ?? {}, first))) return null;
  const evaluatedMode = primaryEvaluatedMode(draft.enabledModes, complete);
  if (evaluatedMode === null) return null;
  return { evaluatedMode, otherModes: modes.filter((m) => m !== evaluatedMode) };
}
